// SPDX-License-Identifier: MIT
// SQL Editor Component - Monaco editor wrapper for SQL editing
// Adapted from Kepler.gl's MonacoEditor pattern

import React, { useCallback, useRef } from 'react';
import Editor, { OnMount, OnChange, loader } from '@monaco-editor/react';
import styled from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { SQL_KEYWORDS, SPATIAL_FUNCTIONS } from '../utils/duckdb-utils';
import * as monaco from 'monaco-editor';

// Configure Monaco to use the npm package instead of CDN
loader.config({ monaco });

// Type for Monaco editor instance (from OnMount callback)
type MonacoEditor = Parameters<OnMount>[0];

// Styled Components
const EditorWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  /* Override Monaco's default background */
  .monaco-editor {
    background: ${props => props.theme.panelBackground} !important;
  }

  .monaco-editor .margin {
    background: ${props => props.theme.panelBackground} !important;
  }

  .monaco-editor-background {
    background: ${props => props.theme.panelBackground} !important;
  }
`;

const LoadingPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${props => props.theme.subtextColor};
  font-size: 14px;
`;

// Props interface
interface SqlEditorProps {
  value: string;
  onChange: (value: string) => void;
  isReadOnly?: boolean;
  theme: DefaultTheme;
  onEscape?: () => void;
}

// Monaco editor options for SQL editing
const EDITOR_OPTIONS = {
  minimap: { enabled: false },
  lineNumbers: 'on' as const,
  lineNumbersMinChars: 3,
  automaticLayout: true,
  wordWrap: 'on' as const,
  scrollBeyondLastLine: false,
  fontSize: 13,
  fontFamily: "'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace",
  tabSize: 2,
  insertSpaces: true,
  folding: true,
  foldingStrategy: 'indentation' as const,
  renderLineHighlight: 'line' as const,
  selectionHighlight: true,
  occurrencesHighlight: 'singleFile' as const,
  cursorBlinking: 'smooth' as const,
  cursorSmoothCaretAnimation: 'on' as const,
  smoothScrolling: true,
  padding: { top: 12, bottom: 12 },
  scrollbar: {
    vertical: 'auto' as const,
    horizontal: 'auto' as const,
    verticalScrollbarSize: 10,
    horizontalScrollbarSize: 10,
  },
  suggestOnTriggerCharacters: true,
  quickSuggestions: {
    other: true,
    comments: false,
    strings: false,
  },
  parameterHints: { enabled: true },
  acceptSuggestionOnEnter: 'on' as const,
  tabCompletion: 'on' as const,
};

export const SqlEditor: React.FC<SqlEditorProps> = ({
  value,
  onChange,
  isReadOnly = false,
  theme,
  onEscape,
}) => {
  const editorRef = useRef<MonacoEditor | null>(null);

  const handleEditorMount: OnMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor;

      // Define custom dark theme to match Integral Analytics brand
      monaco.editor.defineTheme('atlas-dark', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          // SQL keywords in primary bright blue (#339AF0)
          { token: 'keyword', foreground: '339AF0', fontStyle: 'bold' },
          { token: 'keyword.sql', foreground: '339AF0', fontStyle: 'bold' },
          // Strings in yellow (#FFE680)
          { token: 'string', foreground: 'FFE680' },
          { token: 'string.sql', foreground: 'FFE680' },
          // Numbers in medium blue (#7EB6FF)
          { token: 'number', foreground: '7EB6FF' },
          // Comments in cool gray
          { token: 'comment', foreground: 'A8A8A8', fontStyle: 'italic' },
          // Operators in light gray
          { token: 'operator', foreground: 'F2F2F2' },
          // Identifiers in light blue (#CDE5FF)
          { token: 'identifier', foreground: 'CDE5FF' },
          // Types in blue-gray (#7E9DB1)
          { token: 'type', foreground: '7E9DB1' },
          // Functions in secondary mid blue (#78A9FF)
          { token: 'predefined', foreground: '78A9FF', fontStyle: 'bold' },
        ],
        colors: {
          // Background uses our dark theme colors
          'editor.background': '#1A1A1A', // bgDeepGray
          'editor.foreground': '#F2F2F2', // grayLight
          // Line highlight with subtle blue tint
          'editor.lineHighlightBackground': '#003F7420', // primaryDarkBlue with opacity
          // Selection with primary blue
          'editor.selectionBackground': '#005EB860', // primaryDeepBlue with opacity
          'editor.inactiveSelectionBackground': '#005EB830',
          // Cursor in primary bright blue
          'editorCursor.foreground': '#339AF0', // primaryBrightBlue
          // Whitespace in dark gray
          'editorWhitespace.foreground': '#3C3C3C', // grayCharcoal
          // Line numbers
          'editorLineNumber.foreground': '#7D7D7D', // grayMediumDark
          'editorLineNumber.activeForeground': '#339AF0', // primaryBrightBlue
          // Selection highlight with light blue
          'editor.selectionHighlightBackground': '#CDE5FF20', // primaryLightBlue with opacity
        },
      });

      monaco.editor.setTheme('atlas-dark');

      if (onEscape) {
        editor.addCommand(monaco.KeyCode.Escape, () => {
          onEscape();
        });
      }

      // Register SQL language completion provider
      monaco.languages.registerCompletionItemProvider('sql', {
        provideCompletionItems: (model: unknown, position: unknown) => {
          const word = (
            model as {
              getWordUntilPosition: (pos: unknown) => { startColumn: number; endColumn: number };
            }
          ).getWordUntilPosition(position);
          const pos = position as { lineNumber: number };
          const range = {
            startLineNumber: pos.lineNumber,
            endLineNumber: pos.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };

          // SQL keywords
          const keywordSuggestions = SQL_KEYWORDS.map(keyword => ({
            label: keyword,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: keyword,
            range,
            sortText: '1' + keyword, // Sort keywords first
          }));

          // Spatial functions
          const spatialSuggestions = SPATIAL_FUNCTIONS.map(func => ({
            label: func,
            kind: monaco.languages.CompletionItemKind.Function,
            insertText: `${func}()`,
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            detail: 'DuckDB Spatial Function',
            sortText: '2' + func, // Sort functions second
          }));

          return {
            suggestions: [...keywordSuggestions, ...spatialSuggestions],
          };
        },
      });

      // Focus editor
      editor.focus();
    },
    [onEscape]
  );

  const handleChange: OnChange = useCallback(
    value => {
      onChange(value ?? '');
    },
    [onChange]
  );

  return (
    <EditorWrapper theme={theme} data-testid='sql-editor'>
      <Editor
        height='100%'
        defaultLanguage='sql'
        value={value}
        onChange={handleChange}
        onMount={handleEditorMount}
        options={{
          ...EDITOR_OPTIONS,
          readOnly: isReadOnly,
        }}
        loading={<LoadingPlaceholder theme={theme}>Loading editor...</LoadingPlaceholder>}
      />
    </EditorWrapper>
  );
};

export default SqlEditor;
